import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { FurnitureCategory } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg } from "../C1_FurnitureSvg";

type ClimateKind = "ventilation" | "indoor" | "outdoor";
type SymbolProps = {
  width: number;
  height: number;
  detailScale: number;
  strokeWidth: number;
  strokeColor: string;
  fillColor: string;
};

/** Fan blades and guard are physical parts of the device, without a separate icon frame. */
function Fan({ x, y, radius }: { x: number; y: number; radius: number }) {
  return (
    <g>
      <circle cx={x} cy={y} r={radius} />
      <circle cx={x} cy={y} r={radius * 0.85} />
      {[0, 120, 240].map(angle => (
        <path key={angle} transform={`rotate(${angle} ${x} ${y})`}
          d={`M${x - radius * 0.12} ${y - radius * 0.18}C${x - radius * 0.8} ${y - radius * 0.9} ${x + radius * 0.5} ${y - radius * 1.02} ${x + radius * 0.5} ${y - radius * 0.65}C${x + radius * 0.5} ${y - radius * 0.4} ${x + radius * 0.2} ${y - radius * 0.3} ${x + radius * 0.12} ${y - radius * 0.12}`} />
      ))}
      <circle cx={x} cy={y} r={radius * 0.16} />
    </g>
  );
}

function VentilationSymbol({ width, height, detailScale, strokeWidth, strokeColor, fillColor }: SymbolProps) {
  const u = Math.min(detailScale, width / 24, height / 24);
  const fanRadius = Math.max(0, (Math.min(width, height) - 2 * u - strokeWidth) * 0.45);
  return (
    <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x={u} y={u} width={width - 2 * u} height={height - 2 * u} rx={2 * u} fill={fillColor} />
      <Fan x={width / 2} y={height / 2} radius={fanRadius} />
      {[3.5 * u, width - 3.5 * u].flatMap(x => [3.5 * u, height - 3.5 * u].map(y => (
        <path key={`${x}/${y}`} d={`M${x - 0.55 * u} ${y}h${1.1 * u}`} />
      )))}
    </g>
  );
}

function IndoorSymbol({ width, height, detailScale, strokeWidth, strokeColor, fillColor }: SymbolProps) {
  const u = Math.min(detailScale, width / 32, height / 12);
  const bottom = height - u;
  const outletTop = height - 4 * u;
  return (
    <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={`M${3 * u} ${u}H${width - 3 * u}Q${width - u} ${u} ${width - u} ${3 * u}V${bottom - 2 * u}Q${width - u} ${bottom} ${width - 3 * u} ${bottom}H${3 * u}Q${u} ${bottom} ${u} ${bottom - 2 * u}V${3 * u}Q${u} ${u} ${3 * u} ${u}Z`} fill={fillColor} />
      <path d={`M${u} ${outletTop}Q${width / 2} ${outletTop + 1.2 * u} ${width - u} ${outletTop}`} />
      <path d={`M${4 * u} ${bottom - 1.4 * u}H${width - 4 * u}M${4 * u} ${bottom - 2.5 * u}H${width - 4 * u}`} />
      <path d={`M${width - 7 * u} ${outletTop - 2 * u}h${2 * u}`} />
      <circle cx={width - 3.5 * u} cy={outletTop - 2 * u} r={0.35 * u} fill={strokeColor} stroke="none" />
    </g>
  );
}

function OutdoorSymbol({ width, height, detailScale, strokeWidth, strokeColor, fillColor }: SymbolProps) {
  const u = Math.min(detailScale, width / 32, height / 20);
  const bottom = height - 3 * u;
  const divider = width - 9 * u;
  return (
    <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x={u} y={u} width={width - 2 * u} height={bottom - u} rx={u} fill={fillColor} />
      {[4 * u, width - 7 * u].map(x => (
        <path key={x} d={`M${x} ${bottom}v${2 * u}h${3 * u}v${-2 * u}`} />
      ))}
      <Fan x={(u + divider) / 2} y={(u + bottom) / 2} radius={6.5 * u} />
      <path d={`M${divider} ${2.5 * u}V${bottom - 1.5 * u}`} />
      {[0, 1, 2, 3, 4, 5].map(index => (
        <path key={index} d={`M${divider + 2 * u} ${3.5 * u + (bottom - 6 * u) * index / 5}H${width - 3 * u}`} />
      ))}
    </g>
  );
}

const symbols = { ventilation: VentilationSymbol, indoor: IndoorSymbol, outdoor: OutdoorSymbol };
const symbolSizes = { ventilation: [24, 24], indoor: [32, 12], outdoor: [32, 20] } as const;

function ClimateRenderer({ width, height, PX, rotation = 0, strokeWidth = 2,
  strokeColor = "#000000", fillColor = "#ffffff", kind,
}: C1_FurnitureRendererProps & { kind: ClimateKind }) {
  const Symbol = symbols[kind];
  const defaultHeight = kind === "indoor" ? 0.3 : 0.4;
  return (
    <C1_FurnitureSvg PX={PX} width={width} height={height} rotation={rotation}
      viewBoxWidth={width} viewBoxHeight={height} originXPercentage={0} originYPercentage={0}>
      <Symbol width={width} height={height} detailScale={defaultHeight * PX / symbolSizes[kind][1]}
        strokeWidth={strokeWidth} strokeColor={strokeColor} fillColor={fillColor} />
    </C1_FurnitureSvg>
  );
}

function createClimateEquipment(kind: ClimateKind, options: Pick<C1_FurnitureDefinition,
  "id" | "description" | "category" | "additionalCategories">): C1_FurnitureDefinition {
  const Symbol = symbols[kind];
  const [width, height] = symbolSizes[kind];
  return {
    ...options,
    hybridEquipment: true,
    icon: <svg viewBox={`0 0 ${width} ${height}`} fill="none" aria-hidden="true">
      <Symbol width={width} height={height} detailScale={1} strokeWidth={0.85} strokeColor="currentColor" fillColor="none" />
    </svg>,
    defaultWidth: kind === "ventilation" ? 0.4 : 0.8,
    defaultHeight: kind === "indoor" ? 0.3 : kind === "outdoor" ? 0.6 : 0.4,
    autoRotate: true,
    autoReflect: true,
    grabsW: true,
    grabsH: true,
    properties: [],
    Renderer: props => <ClimateRenderer {...props} kind={kind} />,
  };
}

export const Ventilation = createClimateEquipment("ventilation", {
  id: "ventilation",
  description: { de: "Lüftung Innen", en: "Indoor ventilation" },
  category: FurnitureCategory.ElectricalEquipment,
});

export const AirConditioningIndoor = createClimateEquipment("indoor", {
  id: "airConditioningIndoor",
  description: { de: "Klimaanlage Innen", en: "Air conditioning indoor unit" },
  category: FurnitureCategory.ElectricalEquipment,
});

export const AirConditioningOutdoor = createClimateEquipment("outdoor", {
  id: "airConditioningOutdoor",
  description: { de: "Klimaanlage Außen", en: "Air conditioning outdoor unit" },
  category: FurnitureCategory.Outdoor,
});
